import type { SupportRequest } from '../../types/configuration/SupportRequest'
import type { LooseRule } from './SupportFormValidation'

export type SupportFormData = {
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  subject: string
  description: string
  priority: string
  subscriptionId: string
  product: string
  version: string
  errorMessage: string
  attachments: File[]
}

type SupportFormSchema = Partial<Record<keyof SupportFormData, readonly LooseRule[]>>

export function createSupportFormSchema(
  model: SupportRequest | undefined,
  isPriorityVisible: boolean,
  isSubscriptionIdVisible: boolean,
  isVersionRequired: boolean
): SupportFormSchema {
  const limits = {
    email: {
      maxLength: model?.validationRules.email?.maxLength,
    },
    firstName: {
      minLength: model?.validationRules.firstName?.minLength,
      maxLength: model?.validationRules.firstName?.maxLength,
    },
    lastName: {
      minLength: model?.validationRules.lastName?.minLength,
      maxLength: model?.validationRules.lastName?.maxLength,
    },
    subject: {
      minLength: model?.validationRules.subject?.minLength,
      maxLength: model?.validationRules.subject?.maxLength,
    },
    description: {
      minLength: model?.validationRules.description?.minLength,
      maxLength: model?.validationRules.description?.maxLength,
    },
    phoneNumber: {
      minLength: model?.validationRules.phoneNumber?.minLength,
      maxLength: model?.validationRules.phoneNumber?.maxLength,
    },
    version: {
      minLength: model?.validationRules.version?.minLength,
      maxLength: model?.validationRules.version?.maxLength,
    },
    errorMessage: {
      maxLength: model?.validationRules.errorMessage?.maxLength,
    },
  }

  const priorityRules = isPriorityVisible
    ? ([
        { type: 'required', messageKey: { key: 'supportRequest.fields.priority.required' } },
      ] as const)
    : ([] as const)

  const subscriptionIdRules = isSubscriptionIdVisible
    ? ([
        {
          type: 'required',
          messageKey: { key: 'supportRequest.fields.subscriptionId.required' },
        },
      ] as const)
    : ([] as const)

  const versionRules = isVersionRequired
    ? ([
        { type: 'required', messageKey: { key: 'supportRequest.fields.version.required' } },
        {
          type: 'minLength',
          value: limits.version.minLength,
          messageKey: {
            key: 'supportRequest.fields.validation.minLength',
            params: { n: limits.version.minLength },
          },
        },
        {
          type: 'maxLength',
          value: limits.version.maxLength,
          messageKey: {
            key: 'supportRequest.fields.validation.maxLength',
            params: { n: limits.version.maxLength },
          },
        },
      ] as const)
    : ([
        {
          type: 'maxLength',
          value: limits.version.maxLength,
          messageKey: {
            key: 'supportRequest.fields.validation.maxLength',
            params: { n: limits.version.maxLength },
          },
        },
      ] as const)

  return {
    email: [
      { type: 'required', messageKey: { key: 'supportRequest.fields.email.required' } },
      {
        type: 'pattern',
        value: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
        messageKey: { key: 'supportRequest.fields.email.invalid' },
      },
      {
        type: 'maxLength',
        value: limits.email.maxLength,
        messageKey: { key: 'supportRequest.fields.validation.maxLength' },
      },
    ],
    firstName: [
      { type: 'required', messageKey: { key: 'supportRequest.fields.firstName.required' } },
      {
        type: 'minLength',
        value: limits.firstName.minLength,
        messageKey: {
          key: 'supportRequest.fields.validation.minLength',
          params: { n: limits.firstName.minLength },
        },
      },
      {
        type: 'maxLength',
        value: limits.firstName.maxLength,
        messageKey: {
          key: 'supportRequest.fields.validation.maxLength',
          params: { n: limits.firstName.maxLength },
        },
      },
    ],
    lastName: [
      { type: 'required', messageKey: { key: 'supportRequest.fields.lastName.required' } },
      {
        type: 'minLength',
        value: limits.lastName.minLength,
        messageKey: {
          key: 'supportRequest.fields.validation.minLength',
          params: { n: limits.lastName.minLength },
        },
      },
      {
        type: 'maxLength',
        value: limits.lastName.maxLength,
        messageKey: {
          key: 'supportRequest.fields.validation.maxLength',
          params: { n: limits.lastName.maxLength },
        },
      },
    ],
    phoneNumber: [
      {
        type: 'minLength',
        value: limits.phoneNumber.minLength,
        messageKey: {
          key: 'supportRequest.fields.phoneNumber.minLength',
          params: { n: limits.phoneNumber.minLength },
        },
      },
      {
        type: 'maxLength',
        value: limits.phoneNumber.maxLength,
        messageKey: {
          key: 'supportRequest.fields.phoneNumber.maxLength',
          params: { n: limits.phoneNumber.maxLength },
        },
      },
      {
        type: 'pattern',
        value: /^[0-9+\-() ]{1,20}$/,
        messageKey: { key: 'supportRequest.fields.phoneNumber.invalid' },
      },
    ],
    subject: [
      { type: 'required', messageKey: { key: 'supportRequest.fields.subject.required' } },
      {
        type: 'minLength',
        value: limits.subject.minLength,
        messageKey: {
          key: 'supportRequest.fields.validation.minLength',
          params: { n: limits.subject.minLength },
        },
      },
      {
        type: 'maxLength',
        value: limits.subject.maxLength,
        messageKey: {
          key: 'supportRequest.fields.validation.maxLength',
          params: { n: limits.subject.maxLength },
        },
      },
    ],
    description: [
      { type: 'required', messageKey: { key: 'supportRequest.fields.description.required' } },
      {
        type: 'minLength',
        value: limits.description.minLength,
        messageKey: {
          key: 'supportRequest.fields.validation.minLength',
          params: { n: limits.description.minLength },
        },
      },
      {
        type: 'maxLength',
        value: limits.description.maxLength,
        messageKey: {
          key: 'supportRequest.fields.validation.maxLength',
          params: { n: limits.description.maxLength },
        },
      },
    ],
    errorMessage: [
      {
        type: 'maxLength',
        value: limits.errorMessage.maxLength,
        messageKey: {
          key: 'supportRequest.fields.validation.maxLength',
          params: { n: limits.errorMessage.maxLength },
        },
      },
    ],
    version: versionRules,
    priority: priorityRules,
    subscriptionId: subscriptionIdRules,
  } as const
}
